import type { BulkPositionUpdate } from '../types/import-tracking.types';

/**
 * Agrupa el acomodo de bultos sueltos en **una entrada por línea**.
 *
 * **El bug que arregla.** El acomodo automático expande cada línea en sus
 * unidades: 200 teclados salen como 200 bultos con ids `<itemId>_0`, `_1`, …
 * Al guardar, cada bulto se mandaba como una entrada propia con el id de la
 * LÍNEA recortado. O sea: 470 entradas apuntando a 6 filas. El backend recorre
 * y actualiza, así que la fila terminaba con la posición del **último** bulto y
 * las otras 469 se perdían.
 *
 * Eso es lo que se veía en pantalla: auto-empacar acomodaba bien, aparecía
 * "posiciones guardadas", y al recargar quedaba **una caja por producto**. No
 * se borraba nada — nunca se había guardado más que una.
 *
 * Ahora cada línea manda **una** entrada con el acomodo completo en
 * `unitPositions`. `positionX/Y/Z` quedan apuntando a la primera unidad, que es
 * lo que leen las pantallas viejas.
 */

export interface BultoAcomodado {
  /** Id del bulto: `<itemId>` o `<itemId><separador><n>`. */
  id: string;
  x: number;
  y: number;
  z: number;
  rotated: boolean;
}

/**
 * Saca el id de la línea del id de un bulto.
 *
 * Los dos generadores usan separadores distintos —`_` en el empaquetado
 * automático, `#` en el editor manual— así que se cortan los dos. Un UUID no
 * contiene ninguno de los dos caracteres, así que no hay ambigüedad.
 */
export function idDeLinea(idDeBulto: string): string {
  const corte = idDeBulto.search(/[#_]/);
  return corte === -1 ? idDeBulto : idDeBulto.slice(0, corte);
}

/** El número de unidad dentro de la línea, para poder reordenar al recargar. */
function ordinal(idDeBulto: string): number {
  const m = idDeBulto.match(/[#_](\d+)$/);
  return m ? Number(m[1]) : 0;
}

export function agruparUnidades(bultos: BultoAcomodado[]): BulkPositionUpdate['positions'] {
  const porLinea = new Map<string, BultoAcomodado[]>();

  for (const b of bultos) {
    const linea = idDeLinea(b.id);
    const lista = porLinea.get(linea);
    if (lista) lista.push(b);
    else porLinea.set(linea, [b]);
  }

  return [...porLinea.entries()].map(([itemId, unidades]) => {
    // El orden importa: al recargar, la unidad 0 tiene que volver a su lugar.
    unidades.sort((a, b) => ordinal(a.id) - ordinal(b.id));
    const primera = unidades[0];
    return {
      itemId,
      positionX: primera.x,
      positionY: primera.y,
      positionZ: primera.z,
      isRotated: primera.rotated,
      unitPositions: unidades.map((u) => ({ x: u.x, y: u.y, z: u.z, r: u.rotated })),
    };
  });
}
